import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TreeViewNavbar from "../../components/Tree/TreeViewNavbar";
import DateRangeFilter from "../../components/Tree/network-analytics/DateRangeFilter";
import LegFilter from "../../components/Tree/network-analytics/LegFilter";
import OverviewSection from "../../components/Tree/network-analytics/OverviewSection";
import SalesAnalyticsSection from "../../components/Tree/network-analytics/SalesAnalyticsSection";
import NetworkSalesSection from "../../components/Tree/network-analytics/NetworkSalesSection";
import LegPerformanceSection from "../../components/Tree/network-analytics/LegPerformanceSection";
import ProductSalesSection from "../../components/Tree/network-analytics/ProductSalesSection";
import TopPerformersSection from "../../components/Tree/network-analytics/TopPerformersSection";
import { ApiHook } from "../../hooks/apiHook";

const TABS = [
  { key: "overview", label: "overview" },
  { key: "salesAnalytics", label: "salesAnalytics" },
  { key: "sales", label: "networkSales" },
  { key: "legs", label: "legPerformance" },
  { key: "products", label: "productSales" },
  { key: "topPerformers", label: "topPerformers" },
];

const NetworkAnalytics = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialTab = TABS.find((tab) => tab.key === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedLegId, setSelectedLegId] = useState("");

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));

  const startDateISO = useMemo(() => `${startDate}T00:00:00`, [startDate]);
  const endDateISO = useMemo(() => `${endDate}T23:59:59`, [endDate]);

  const needsLegs = activeTab === "legs" || activeTab === "products" || activeTab === "topPerformers" || activeTab === "sales";
  const legsQuery = ApiHook.CallNetworkLegs(
    needsLegs ? startDateISO : null,
    needsLegs ? endDateISO : null
  );

  const legs = legsQuery?.data?.data?.legs || [];

  const showLegFilter = activeTab === "products" || activeTab === "topPerformers" || activeTab === "sales";

  return (
    <>
      <TreeViewNavbar menu="networkAnalytics" />

      <div className="network-analytics-page">
        {activeTab !== "salesAnalytics" && (
          <div className="row mb-3 mt-3 align-items-end">
            <div className={showLegFilter ? "col-md-8" : "col-md-12"}>
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                showAllTime
              />
            </div>
            {showLegFilter && (
              <div className="col-md-4">
                <LegFilter
                  legs={legs}
                  selectedLegId={selectedLegId}
                  setSelectedLegId={setSelectedLegId}
                />
              </div>
            )}
          </div>
        )}

        <div className="tree_view_top_filter_bar mb-3">
          <div className="row">
            <div className="col-12">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`btn_ewallt_page ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {t(tab.label)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="tab-content-area">
          {activeTab === "overview" && (
            <OverviewSection
              startDate={startDateISO}
              endDate={endDateISO}
            />
          )}
          {activeTab === "salesAnalytics" && (
            <SalesAnalyticsSection
              legs={legs}
            />
          )}
          {activeTab === "sales" && (
            <NetworkSalesSection
              startDate={startDateISO}
              endDate={endDateISO}
              legId={selectedLegId}
            />
          )}
          {activeTab === "legs" && (
            <LegPerformanceSection
              legs={legs}
              isLoading={legsQuery.isLoading}
              isFetching={legsQuery.isFetching}
              startDate={startDateISO}
              endDate={endDateISO}
            />
          )}
          {activeTab === "products" && (
            <ProductSalesSection
              startDate={startDateISO}
              endDate={endDateISO}
              legId={selectedLegId}
            />
          )}
          {activeTab === "topPerformers" && (
            <TopPerformersSection
              startDate={startDateISO}
              endDate={endDateISO}
              legId={selectedLegId}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default NetworkAnalytics;
